// ЕДИНСТВЕННЫЙ писатель карты доступности модулей (§6.4).
//
// Монтируется РОВНО ОДИН раз — в `__root.tsx`, рядом с
// `RealtimeInvalidationProvider`. Второй инстанс не сломает стор (запись
// идемпотентна и защищена сверкой владельца и поколения), но заведёт вторую гонку «ответ сервера ↔ смена аккаунта» на
// ровном месте — а именно этой гонкой и вызван инцидент `40b62b3b`.
//
// Инвалидация без перезагрузки: событие `modules:availability_changed` стоит
// ОДНОЙ записью в `lib/realtime/invalidation-map.ts` (канон), она инвалидирует
// ключ `["modules"]` → запрос перезапрашивается → эффект ниже переписывает
// снапшот. Отдельной socket-подписки здесь нет специально: две подписки на одно
// событие — это два разных момента истины о том, когда карта устарела.

import { useEffect } from "react";
import { useSession } from "@/hooks/use-session";
import { CLIENT_MODULE_PLATFORM } from "@/lib/module-availability/platform";
import {
	clearModuleAvailabilitySnapshot,
	writeModuleAvailabilitySnapshot,
} from "@/lib/module-availability/snapshot";
import { useModuleAvailabilityStore } from "@/lib/module-availability/store";
import { api } from "@/lib/trpc-client";

/**
 * Запас свежести, пока сервер не ответил ни разу.
 *
 * Дальше срок берётся из ответа (`ttlSeconds`): сервер знает про собственный
 * кэш больше клиента, и второе число здесь было бы вторым источником правды о
 * свежести. Основной канал доставки изменений всё равно событийный; это
 * страховка на случай пропущенного сокет-события (сон вкладки, обрыв на
 * мобильной сети), она же держит метрику М4 (≤60 секунд).
 */
const FALLBACK_STALE_MS = 60_000;

export function ModuleAvailabilityProvider(): null {
	const { user, isAuthenticated, isSessionPending } = useSession();
	const userId = user?.id ?? null;
	const applyServer = useModuleAvailabilityStore((s) => s.applyServer);
	const reset = useModuleAvailabilityStore((s) => s.reset);
	const ownerUserId = useModuleAvailabilityStore((s) => s.ownerUserId);
	const ttlSeconds = useModuleAvailabilityStore((s) => s.ttlSeconds);

	const query = api.modules.availability.useQuery(
		{ platform: CLIENT_MODULE_PLATFORM },
		{
			// Не стрелять, пока auth.me после mount не подтвердил сессию — иначе
			// stale persist/bearer → 401 → «Сессия истекла» на лендинге.
			enabled: isAuthenticated && !isSessionPending,
			staleTime: ttlSeconds === null ? FALLBACK_STALE_MS : ttlSeconds * 1000,
			// Карта обязана пережить обрыв: офлайн отвечает снапшот, а не пустота.
			retry: 1,
			refetchOnReconnect: true,
		},
	);

	// Смена аккаунта в той же вкладке (и выход): чужая карта и чужой снапшот
	// снимаются немедленно. Проверка по id, а не по факту «залогинен» — вход под
	// другим пользователем не меняет булев признак, но меняет ВСЁ содержимое карты.
	useEffect(() => {
		if (ownerUserId === null) return;
		if (ownerUserId === userId) return;
		reset();
		clearModuleAvailabilitySnapshot();
	}, [ownerUserId, userId, reset]);

	// Ответ сервера → стор + снапшот. Сверка `userId` здесь и внутри `applyServer`
	// не дублирование: эффект отсекает ответ «из прошлой жизни» до записи в
	// хранилище, стор — до записи в память (он же не пускает более старое
	// поколение карты поверх более нового).
	const data = query.data;
	useEffect(() => {
		if (!data || !userId) return;
		if (data.userId !== userId) return;
		applyServer(data, userId);
		writeModuleAvailabilitySnapshot(data);
	}, [data, userId, applyServer]);

	return null;
}
